import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  App,
  Row,
  Col,
  Select,
  DatePicker,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { listCities, listSpecialties, listStates } from '../../../services/catalogs';
import { listSpecialistsReport } from '../../../services/reports';
import { isoToDMY } from '../../../utils/date';
import { exportToExcel } from '../../../utils/excel';
import { getMembershipTypesDropdown } from '../../../services/memberships';
import { Input } from 'antd';

const { Title } = Typography;
const { useApp } = App;

const SpecialistReportPage = () => {
  const navigate = useNavigate();
  const { message } = useApp();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  });

  /** =========================
   *  FILTROS
   ========================= */
  const getDefaultFilters = () => ({
    search: '',
    startDate: null,
    endDate: null,
    registrationDate: null,
    specialty: null,
    state: null,
    city: null,
    membershipType: null,
  });

  const [filters, setFilters] = useState(getDefaultFilters);

  /** =========================
   *  CATÁLOGOS
   ========================= */
  const [states, setStates] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [
          statesData,
          specialtiesData,
          membershipsData,
        ] = await Promise.all([
          listStates(),
          listSpecialties(),
          getMembershipTypesDropdown(),
        ]);
        setStates(statesData || []);
        setSpecialties(specialtiesData?.specialties || []);
        setMembershipTypes(membershipsData?.data || []);
      } catch (error) {
        message.error('No se pudieron cargar los catálogos');
      }
    };

    loadCatalogs();
  }, [message]);

  /** =========================
   *  MUNICIPIOS DEPENDIENTES
   ========================= */
  useEffect(() => {
    const loadMunicipalities = async () => {
      if (!filters.state) {
        setMunicipalities([]);
        setFilters(prev => ({ ...prev, city: null }));
        return;
      }

      setLoadingMunicipalities(true);
      try {
        const data = await listCities(filters.state);
        setMunicipalities(data || []);
      } catch {
        message.error('No se pudieron cargar las ciudades');
      } finally {
        setLoadingMunicipalities(false);
      }
    };

    loadMunicipalities();
  }, [filters.state, message]);

  /** =========================
   *  FETCH DATA
   ========================= */
  const fetchData = useCallback(async (page = 1, pageSize = pagination.pageSize, currentFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        search: currentFilters.search || undefined,
        startDate: currentFilters.startDate?.format('YYYY-MM-DD'),
        endDate: currentFilters.endDate?.format('YYYY-MM-DD'),
        registrationDate: currentFilters.registrationDate?.format('YYYY-MM-DD'),
        specialty: currentFilters.specialty,
        state: currentFilters.state,
        city: currentFilters.city,
        membershipType: currentFilters.membershipType,
        page,
        pageSize,
      };

      const { items, total } = await listSpecialistsReport(params);
      console.log(items, total)
      setData(items || []);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
      });
    } catch(error) {
      console.log(error)
      message.error('No se pudo cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, message]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  /** =========================
   *  ACCIONES
   ========================= */
  const handleFilterChange = (key: string, value: any) => {
    const newValue = value?.target?.value ?? value;
    setFilters(prev => ({ ...prev, [key]: newValue }));
  };

  const handleSearch = () => {
    if (!filters.startDate || !filters.endDate) {
      message.warning('Fecha inicio y fecha fin de la membresía son obligatorias');
      return;
    }

    fetchData(1);
  };

  const handleExportToExcel = async () => {
  setExporting(true);
  try {
    if (!filters.startDate || !filters.endDate) {
      message.warning('Fecha inicio y fecha fin de la membresía son obligatorias');
      return;
    }

    const params = {
      startDate: filters.startDate.format('YYYY-MM-DD'),
      endDate: filters.endDate.format('YYYY-MM-DD'),
      registrationDate: filters.registrationDate?.format('YYYY-MM-DD'),
      specialty: filters.specialty,
      state: filters.state,
      city: filters.city,
      membershipType: filters.membershipType,
      page: 1,
      pageSize: 1000, // límite razonable para exportar
    };

    const { items } = await listSpecialistsReport(params);

    if (!items || items.length === 0) {
      message.warning('No hay datos para exportar');
      return;
    }

    const excelData = items.map((item, idx) => ({
      '#': idx + 1,
      Nombre: item.first_name ?? '-',
      Apellido: item.last_name ?? '-',
      Correo: item.email ?? '-',
      Especialidad: item.specialty ?? '-',
      'Cédula Profesional': item.professional_license ?? '-',
      Ciudad: item.city ?? '-',
      Estado: item.state ?? '-',
      Membresía: item.membership ?? '-',
      'Inicio Membresía': isoToDMY(item.membership_start) ?? '-',
      'Fin Membresía': isoToDMY(item.membership_end) ?? '-',
    }));

    const columnWidths = [
      { wch: 5 },  // #
      { wch: 18 }, // Nombre
      { wch: 18 }, // Apellido
      { wch: 30 }, // Correo
      { wch: 22 }, // Especialidad
      { wch: 22 }, // Cédula
      { wch: 18 }, // Ciudad
      { wch: 18 }, // Estado
      { wch: 18 }, // Membresía
      { wch: 18 }, // Inicio
      { wch: 18 }, // Fin
    ];

    exportToExcel(
      excelData,
      columnWidths,
      'Reporte Especialistas',
      'reporte_especialistas'
    );
  } catch (err) {
    console.error('Error al exportar a Excel:', err);
    message.error('No se pudo exportar el reporte');
  } finally {
    setExporting(false);
  }
};


  const handleClearFilters = () => {
    const reset = getDefaultFilters();
    setFilters(reset);
    fetchData(1, pagination.pageSize, reset);
  };

  /** =========================
   *  COLUMNAS
   ========================= */
  const columns = [
    { title: 'Nombre', dataIndex: 'first_name' },
    { title: 'Apellido', dataIndex: 'last_name' },
    { title: 'Correo', dataIndex: 'email' },
    { title: 'Especialidad', dataIndex: 'specialty' },
    { title: 'Cédula Profesional', dataIndex: 'professional_license' },
    { title: 'Ciudad', dataIndex: 'city' },
    { title: 'Estado', dataIndex: 'state' },
    { title: 'Membresía', dataIndex: 'membership' },
    {
      title: 'Fecha Inicio Membresía',
      dataIndex: 'membership_start',
      render: isoToDMY,
    },
    {
      title: 'Fecha Fin Membresía',
      dataIndex: 'membership_end',
      render: isoToDMY,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />
            <Title level={3} style={{ margin: 0 }}>
              Reporte Especialista
            </Title>
          </Space>
        }
        extra={
          <Tooltip title="Exportar a Excel">
            <Button icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exporting}
              disabled={exporting}
            />
          </Tooltip>
        }
      >
        {/* ================= FILTROS ================= */}
        <Card type="inner" title="Filtros" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <span>Nombre</span>
              <Input
                style={{ width: '100%' }}
                value={filters.search}
                onChange={d => handleFilterChange('search', d)}
              />
            </Col>
            <Col md={8}>
              <span>Fecha Inicio (Membresía) *</span>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                value={filters.startDate}
                onChange={d => handleFilterChange('startDate', d)}
                disabledDate={current =>
                  filters.endDate && current > dayjs(filters.endDate).endOf('day')
                }
              />
            </Col>

            <Col md={8}>
              <span>Fecha Fin (Membresía) *</span>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                value={filters.endDate}
                onChange={d => handleFilterChange('endDate', d)}
                disabledDate={current =>
                  filters.startDate && current < dayjs(filters.startDate).startOf('day')
                }
              />
            </Col>

            <Col md={8}>
              <span>Fecha de Registro</span>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                value={filters.registrationDate}
                onChange={d => handleFilterChange('registrationDate', d)}
              />
            </Col>

            <Col md={8}>
              <span>Especialidad</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                options={specialties.map(s => ({
                  value: s.id,
                  label: s.label,
                }))}
                value={filters.specialty}
                onChange={v => handleFilterChange('specialty', v)}
              />
            </Col>

            <Col md={8}>
              <span>Estado</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                options={states.map(s => ({
                  value: s.idEstado,
                  label: s.estado,
                }))}
                value={filters.state}
                onChange={v => handleFilterChange('state', v)}
              />
            </Col>

            <Col md={8}>
              <span>Ciudad</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                disabled={!filters.state}
                loading={loadingMunicipalities}
                options={municipalities.map(c => ({
                  value: c.idMunicipio,
                  label: c.municipio,
                }))}
                value={filters.city}
                onChange={v => handleFilterChange('city', v)}
              />
            </Col>

            <Col md={8}>
              <span>Tipo de Membresía</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                options={membershipTypes.map(m => ({
                  value: m.id,
                  label: m.name,
                }))}
                value={filters.membershipType}
                onChange={v => handleFilterChange('membershipType', v)}
              />
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                shape="circle"
                size="large"
                onClick={handleSearch}
              />
              <Button
                icon={<ClearOutlined />}
                shape="circle"
                size="large"
                onClick={handleClearFilters}
              />
            </Space>
          </Row>
        </Card>

        {/* ================= TABLA ================= */}
        <Table
          rowKey={r => r.id}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={pag => fetchData(pag.current, pag.pageSize)}
          
        />
      </Card>
    </div>
  );
};

export default SpecialistReportPage;
